/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { useEffect, useState } from 'react';
import { Image, TouchableOpacity, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { pd } from '@airasia-rn/ui';
import IconView from '@airasia-phoenix/react-native/components/IconView';
import SkeletonLoaderView from '@airasia-phoenix/react-native/components/SkeletonLoaderView';
import type { FeedItemProps } from './types';
import { getColor } from '@airasia-phoenix/react-native/utils';

// Hard cap on the per-tile image shimmer. If a thumbnail request hangs (slow
// CDN, dropped connection) the onLoad/onError callbacks may never fire; without
// a cap the tile shimmers forever — and a grid of shimmering tiles is visually
// identical to the feed's loading skeleton, which reads as "data never loaded"
// even though the feed itself rendered fine. After the cap the tile degrades to
// the static placeholder background instead.
const IMAGE_SHIMMER_MAX_MS = 8000;

export const FeedItemView = ({
  post,
  onPress,
  itemWidth = 0,
  isTopLeft,
  isTopRight,
  isBottomLeft,
  isBottomRight,
  disableShimmer = false,
}: FeedItemProps) => {
  const [isLoading, setIsLoading] = useState(!disableShimmer);

  useEffect(() => {
    if (!isLoading) return;
    const timer = setTimeout(() => setIsLoading(false), IMAGE_SHIMMER_MAX_MS);
    return () => clearTimeout(timer);
  }, [isLoading]);

  const handlePress = () => {
    if (onPress) {
      onPress(post);
    }
  };

  const thumbnailUrl = post.media.thumbnail?.url;
  const isVideo = post.media.type === 'VIDEO';

  const dynamicBorderRadius = {
    borderTopLeftRadius: isTopLeft ? 16 : 0,
    borderTopRightRadius: isTopRight ? 16 : 0,
    borderBottomLeftRadius: isBottomLeft ? 16 : 0,
    borderBottomRightRadius: isBottomRight ? 16 : 0,
  };

  return (
    <TouchableOpacity
      onPress={handlePress}
      disabled={!onPress}
      testID="feed-item-touchable"
    >
      <View
        style={[styles.container, { width: itemWidth }, dynamicBorderRadius]}
      >
        {thumbnailUrl ? (
          <>
            <Image
              source={{ uri: thumbnailUrl }}
              style={[
                styles.thumbnail,
                { width: itemWidth },
                dynamicBorderRadius,
              ]}
              resizeMode="cover"
              onLoadEnd={() => setIsLoading(false)}
              onLoad={() => setIsLoading(false)}
              onError={() => setIsLoading(false)}
            />
            {isLoading && (
              <View
                style={[
                  styles.skeleton,
                  { width: itemWidth },
                  dynamicBorderRadius,
                ]}
                pointerEvents="none"
              >
                <SkeletonLoaderView style={{ flex: 1 }} />
              </View>
            )}
          </>
        ) : null}
        {isVideo && (
          <View style={styles.playIconBadge}>
            <IconView
              icon={pd.icon.custom.video_play}
              width={24}
              height={24}
              tint={getColor(pd.sys.colour.inline_white)}
            />
          </View>
        )}
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create((pd) => ({
  container: {
    height: 200,
    overflow: 'hidden' as const,
    backgroundColor: pd.sys.colour.container_low.toString(),
  },
  thumbnail: {
    height: 200,
  },
  skeleton: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    height: 200,
  },
  playIconBadge: {
    position: 'absolute' as const,
    right: 4,
    bottom: 4,
    width: 32,
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: pd.sys.colour.container_high.toString(),
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.7,
    shadowRadius: 4,
    elevation: 4,
  },
}));
